import React, { useEffect, useRef, useState } from 'react';
import { useProduct } from '../../../hooks/useProduct';
import { useProductCategories } from '../../../hooks/useProductCategories';
import { useProductUnits } from '../../../hooks/useProductUnits';
import { useItemType } from '../../../hooks/item/useItemType';
import FormModal from '../../../components/Modals/FormModal';
import ProductItemTypeSelector from '../../../components/Product/ProductForm/ProductItemTypeSelector';
import ProductBasicInfo from '../../../components/Product/ProductForm/ProductBasicInfo';
import ServiceFields from '../../../components/Product/ProductForm/ServiceFields';
import ProductConfiguration from '../../../components/Product/ProductForm/ProductConfiguration';
import ProductImageUpload from '../../../components/Product/ProductForm/ProductImageUpload';
import ProductSubmitButton from '../../../components/Product/ProductForm/ProductSubmitButton';
import SectionCollapsibleCard from '../../FormSections/SectionCollapsibleCard';
import ProductAttributesVariant from '../../Product/form-sections/ProductAttributesVariant';
import ProductConsumableSection from '../../Product/form-sections/ProductConsumableSection';
import ProductDimensionsSection from '../../Product/form-sections/ProductDimensionsSection';
import ProductSpecialConditionsSection from '../../Product/form-sections/ProductSpecialConditionsSection';
import ProductStorageLocationSection from '../../Product/form-sections/ProductStorageLocationSection';
import ProductTraceability from '../../Product/form-sections/ProductTraceability';
import ProductExternalIdentifiersSection from '../../Product/form-sections/ProductExternalIdentifiersSection';
import ProductCommercialClassificationSection from '../../Product/form-sections/ProductCommercialClassificationSection';
import { useBrands } from '../../../hooks/product/useBrands';
import { useProductSubCategories } from '../../../hooks/product/useProductSubCategories';
import ProductInitialInventorySection from '../../Product/form-sections/ProductInitialInventorySection';
import ProductAdditionalCharges from '../../Product/ProductForm/ProductAdditionalCharges';
import ProductStockControlSection from '../../Product/form-sections/ProductStockControlSection';
import ProductDiscountSection from '../../Product/form-sections/ProductDiscountSection';
import useSystemFeatures from '../../../hooks/system/useSystemFeatures';
import { SYSTEM_FEATURE } from '../../../types/system/features';

interface ProductRegistrationPopupProps {
  popupOpen: boolean;
  onClose: () => void;
}

const ProductRegistrationPopup: React.FC<ProductRegistrationPopupProps> = ({
  popupOpen,
  onClose,
}) => {
  const { form, updateField, isSubmitting, handlerAddOrUpdateProduct, formErrors } =
    useProduct();

  // 🔽 Fuerza la expansión de las secciones colapsables y hace scroll hasta
  // el primer campo con error cada vez que falla la validación.
  const [errorToken, setErrorToken] = useState(0);
  const previousErrorKeysRef = useRef<string>('');

  useEffect(() => {
    const errorKeys = Object.keys(formErrors);
    if (errorKeys.length === 0) return;

    const signature = errorKeys.join('|');
    if (signature === previousErrorKeysRef.current) return;
    previousErrorKeysRef.current = signature;

    setErrorToken((token) => token + 1);

    const firstErrorKey = errorKeys[0];
    const timeoutId = window.setTimeout(() => {
      const target = document.querySelector(
        `[data-field="${firstErrorKey}"]`
      );
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [formErrors]);

  const { isEnabled } = useSystemFeatures();
  const showRestaurantCharges = isEnabled(SYSTEM_FEATURE.RESTAURANT);

  const {
    fetchProductCategoriesDropdown,
    productCategoriesDropdown: categoryOptions,
  } = useProductCategories();

  const {
    handlerAddProductUnit,
    fetchProductUnitsDropdown,
    productUnitsDropdown: unitOptions,
  } = useProductUnits();

  const { brandsDropdown, fetchBrandsDropdown } = useBrands();

  const { subCategories, loadSubCategoriesByCategory } =
    useProductSubCategories();

  const { itemTypeDropdown, fetchItemTypeDropdown } = useItemType();

  // 🔁 Al abrir modal: cargar catálogos
  useEffect(() => {
    if (popupOpen) {
      fetchBrandsDropdown();
      fetchProductCategoriesDropdown();
      fetchProductUnitsDropdown();
      fetchItemTypeDropdown();
    }
  }, [popupOpen]);

  // 📌 Determinar si es un servicio
  const selectedItemType = itemTypeDropdown.find(
    (i) => i.id === form.itemTypeId
  );
  const isService = selectedItemType?.code === 2;

  return (
    <FormModal
      isOpen={popupOpen}
      onClose={onClose}
      title={`Registrar ${form.itemTypeCode === 2 ? 'Servicio' : 'Producto'}`}
      maxWidth="max-w-6xl"
    >
      <div className="flex flex-col gap-8">
        {/* Selección de tipo de ítem */}
        <ProductItemTypeSelector
          value={form.itemTypeId}
          options={itemTypeDropdown}
          onChange={({ id, code }) => {
            updateField('itemTypeId', id);
            updateField('itemTypeCode', code);
          }}
        />

        {/* Campos dinámicos según tipo */}
        {!isService ? (
          <ProductBasicInfo
            form={form}
            onChange={(field, value) => updateField(field, value)}
          />
        ) : (
          <ServiceFields
            form={form}
            onChange={(field, value) => updateField(field, value)}
          />
        )}

        {/* Configuración adicional */}
        <ProductConfiguration
          form={form}
          unitOptions={unitOptions}
          onChange={(field, value) => updateField(field, value)}
          onReloadUnits={fetchProductUnitsDropdown}
          onUnitCreated={handlerAddProductUnit}
        />

        {showRestaurantCharges && (
          <SectionCollapsibleCard
            title="Cargos adicionales"
            message="Clasifica la participación del producto en propina y turismo."
            defaultOpen
            forceOpenToken={errorToken}
          >
            <ProductAdditionalCharges form={form} onChange={updateField} />
          </SectionCollapsibleCard>
        )}

        {/* ===================================================== */
        /* 🔹 Inventario Inicial */
        /* ===================================================== */}

        {!isService && (
          <SectionCollapsibleCard
            title="Inventario Inicial"
            message="
            Permite registrar existencias iniciales automáticamente
            al crear el producto.
          "
            forceOpenToken={errorToken}
          >
            <ProductInitialInventorySection
              form={form}
              onChange={updateField}
            />
          </SectionCollapsibleCard>
        )}

        <SectionCollapsibleCard
          title="Clasificación Comercial"
          message="Permite clasificar productos y servicios dentro del catálogo."
          defaultOpen
          forceOpenToken={errorToken}
        >
          <ProductCommercialClassificationSection
            form={form}
            onChange={updateField}
            brandOptions={brandsDropdown}
            categoryOptions={categoryOptions}
            subCategoryOptions={subCategories}
            loadSubCategoriesByCategory={loadSubCategoriesByCategory}
          />
        </SectionCollapsibleCard>

        {!isService && (
          <SectionCollapsibleCard
            title="Control de Inventario"
            message="Define si este producto debe controlarse estrictamente, sin importar la configuración general de ventas con stock negativo."
            forceOpenToken={errorToken}
          >
            <ProductStockControlSection form={form} onChange={updateField} />
          </SectionCollapsibleCard>
        )}

        <SectionCollapsibleCard
          title="Descuentos"
          message="Define si este producto puede recibir descuentos (manuales o el descuento global de la venta/cotización)."
          forceOpenToken={errorToken}
        >
          <ProductDiscountSection form={form} onChange={updateField} />
        </SectionCollapsibleCard>

        {/* Subida de imagen */}
        <ProductImageUpload
          file={form.file}
          onUpload={(file) => updateField('file', file)}
          onDelete={() => updateField('file', null)}
        />

        {!isService ? (
          <>
            <SectionCollapsibleCard
              title="Identificadores Externos"
              message="Define códigos alternativos para compatibilidad con inventarios, proveedores y sistemas externos."
              forceOpenToken={errorToken}
            >
              <ProductExternalIdentifiersSection
                form={form}
                onChange={updateField}
              />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Trazabilidad del producto"
              message="Control de lote, batch y fecha de vencimiento para seguimiento e inventario."
              forceOpenToken={errorToken}
            >
              <ProductTraceability form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Atributos y Variantes"
              message="Característica opcional del producto"
              forceOpenToken={errorToken}
            >
              <ProductAttributesVariant form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Consumible"
              message="Indica si el producto se consume al usarse"
              forceOpenToken={errorToken}
            >
              <ProductConsumableSection form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Dimensiones y Logística"
              message="Información útil para almacenamiento, empaque y logística."
              forceOpenToken={errorToken}
            >
              <ProductDimensionsSection form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Condiciones Especiales"
              message="Afectan almacenamiento, logística y manipulación."
              forceOpenToken={errorToken}
            >
              <ProductSpecialConditionsSection
                form={form}
                onChange={updateField}
              />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Ubicación Física"
              message="Datos para localizar físicamente el producto en almacén."
              forceOpenToken={errorToken}
            >
              <ProductStorageLocationSection
                form={form}
                onChange={updateField}
              />
            </SectionCollapsibleCard>
          </>
        ) : null}

        {/* Botón de guardar */}
        <ProductSubmitButton
          formId={form.id}
          isSubmitting={isSubmitting}
          itemTypeCode={form.itemTypeCode}
          onClick={() => handlerAddOrUpdateProduct({ ...form }, form.file)}
        />
      </div>
    </FormModal>
  );
};

export default ProductRegistrationPopup;
